import { ReactElement, useMemo, useState } from "react";
import {
  UsePaginatedPostsConfig,
  usePaginatedPosts,
} from "~/lib/client/usePaginatedPosts";
import { ReadMoreItem, apiToReadMorePosts } from "./apiToReadMorePosts";

type WithDisplayFlags = {
  displayDate?: boolean;
  displayKicker?: boolean;
  displayFooter?: boolean;
};

export type ReadMorePostsRenderProps = WithDisplayFlags & {
  contentItems: ReadMoreItem[];
  isLoading: boolean;
  onLoadMore: () => void;
  showSkeleton: boolean;
  showLoadMore: boolean;
};

export type ReadMorePostsProps = WithDisplayFlags & {
  usePaginatedPostsConfig: UsePaginatedPostsConfig;
  defaultItems: ReadMoreItem[];
  renderItems: (
    renderProps: ReadMorePostsRenderProps
  ) => ReactElement<any, any>;
  totalPages: number;
  title?: string;

  /** These three display flags are passed through as render props */
  displayDate?: boolean;
  displayKicker?: boolean;
  displayFooter?: boolean;
};

export const ReadMorePosts: React.FC<ReadMorePostsProps> = ({
  renderItems,
  usePaginatedPostsConfig,
  defaultItems,
  totalPages,
  displayDate,
  displayFooter,
  displayKicker,
}) => {
  const { posts, isLoadingPosts, isLoadingMore, loadNextPage, currentPage } =
    usePaginatedPosts(usePaginatedPostsConfig);
  const [isFirstTimeLoading, setIsFirstTimeLoading] = useState(true);

  const isLoading = isLoadingPosts || isLoadingMore;

  // This prevents a flash of the loading skeleton on first load
  if (isFirstTimeLoading && !isLoadingPosts) {
    setIsFirstTimeLoading(false);
  }

  const contentItems = useMemo<ReadMoreItem[]>(
    () => (isLoadingPosts ? defaultItems : apiToReadMorePosts(posts)),
    [posts, defaultItems, isLoadingPosts]
  );

  return renderItems({
    contentItems,
    showSkeleton: !isFirstTimeLoading && isLoading,
    showLoadMore: currentPage < totalPages!,
    onLoadMore: loadNextPage,
    isLoading,
    displayDate,
    displayFooter,
    displayKicker,
  });
};
